import { useTranslation } from 'react-i18next';
import {
  WINDOWS_MT5_GL_SMOOTH_DOWNLOAD_LINK,
  WINDOWS_MT5_OFFICIAL_DOWNLOAD_LINK,
} from '../../utils/windowsDownload';

type WindowsMt5DownloadModalProps = {
  open: boolean;
  onClose: () => void;
};

export default function WindowsMt5DownloadModal({ open, onClose }: WindowsMt5DownloadModalProps) {
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4 py-8">
      <div className="relative w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl md:p-8">
        <button
          type="button"
          aria-label={t('common.close')}
          onClick={onClose}
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition hover:bg-gray-200 hover:text-gray-900"
        >
          <i className="ri-close-line text-2xl" />
        </button>

        <div className="mb-6 pr-10">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-700">
            <i className="ri-windows-fill" />
            {t('windowsDownload.badge')}
          </div>
          <h3 className="mb-3 text-2xl font-bold text-gray-900 md:text-3xl">
            {t('windowsDownload.title')}
          </h3>
          <p className="text-sm leading-7 text-gray-600 md:text-base">
            {t('windowsDownload.description')}
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <a
            href={WINDOWS_MT5_GL_SMOOTH_DOWNLOAD_LINK}
            className="group rounded-2xl border-2 border-amber-500 bg-gradient-to-br from-amber-50 to-yellow-50 p-5 transition hover:-translate-y-1 hover:shadow-xl"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500 text-white transition group-hover:scale-110">
              <i className="ri-flashlight-fill text-2xl" />
            </div>
            <h4 className="mb-2 text-lg font-bold text-gray-900">{t('windowsDownload.smoothTitle')}</h4>
            <p className="mb-4 text-sm leading-6 text-gray-600">
              {t('windowsDownload.smoothDesc')}
            </p>
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-white">
              {t('windowsDownload.smoothButton')}
              <i className="ri-download-line" />
            </span>
          </a>

          <a
            href={WINDOWS_MT5_OFFICIAL_DOWNLOAD_LINK}
            className="group rounded-2xl border border-gray-200 bg-white p-5 transition hover:-translate-y-1 hover:border-gray-300 hover:shadow-xl"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-900 text-white transition group-hover:scale-110">
              <i className="ri-global-line text-2xl" />
            </div>
            <h4 className="mb-2 text-lg font-bold text-gray-900">{t('windowsDownload.officialTitle')}</h4>
            <p className="mb-4 text-sm leading-6 text-gray-600">
              {t('windowsDownload.officialDesc')}
            </p>
            <span className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white">
              {t('windowsDownload.officialButton')}
              <i className="ri-external-link-line" />
            </span>
          </a>
        </div>
      </div>
    </div>
  );
}
